/**
 * What a GM actually controls, for the trade form to offer.
 *
 * Tim, 1 September: "player selection should list players that the GM currently has control
 * over (i.e. rostered players, farm players [trade rights, if other GM has space], keeper players
 * {before draft, if receiving GM doesn't go over keeper cap and can pay for any penalties])".
 *
 * The name field used to suggest from the whole NFL bank, which is right for spelling and wrong
 * for the question a trade form asks: a man cannot send what he does not hold. So the first
 * things offered are his — rostered players off the keeper file, farm rights off the farm log —
 * each labelled with what it costs the other side to take it. The bank stays behind as a
 * fallback, because the league has traded players the file has never heard of and a form that
 * refuses them cannot file the trade two men just agreed to.
 *
 * KEEPERS TRAVEL WITH THEIR STANDING (2013-III-3, 2018-III-2). A declared keeper is offered as a
 * keeper: his cost round, and the fact that the receiving GM will be keeping him. After the
 * keeper deadline a designated keeper cannot be un-kept by either side, so what the receiver is
 * taking on is a slate entry as much as a player — see `tradeKeepers.keeperEffect` for what that
 * does to the rounds, and `tradeKeepers.slateAfterTrade` for what it does to the count.
 */

import type { KeeperDeclaration } from '../store/types.js';

export interface RosterRow {
  player: string;
  team: string;
  pos: string;
  cost: number | null;
  tagCost: number | null;
}

export interface FarmRow {
  player: string;
  gm: string;
  pos?: string;
  school?: string;
  status: string;
  active?: boolean;
}

export interface Controlled {
  name: string;
  pos: string | null;
  /** "keeper · costs Rd 5" / "farm · Oregon" / "" — what the row means to the other side. */
  note: string;
  keeper: boolean;
  /** The round a keeper costs, tag honoured. Null for a man who is not a declared keeper. */
  costRound: number | null;
  /** What the asset becomes when he is picked: a rostered man or farm rights. */
  kind: 'player' | 'rights';
}

/**
 * Where the season is, for what a GM may put in a deal.
 *
 * Tim, 2 September: "the options there should include any farm players I have rights to, any
 * potential or declared keepers I have (depending on where we are in the timeline), or any
 * rostered players if we're in-season."
 *
 *   before the keeper deadline   every keeper-eligible man — a potential keeper
 *   after it, before the draft   the declared slate only — the rest go back in the pool
 *   in season                    the whole roster
 *
 * Farm rights are on the list in every phase; they are the one asset that does not turn over.
 */
export type TradeWindowPhase = 'keepers-open' | 'keepers-locked' | 'in-season';

/** Rostered players he may put in a deal in this phase, keepers first, each saying what he is. */
export function controlledPlayers(
  gm: string,
  rows: readonly RosterRow[],
  decl: KeeperDeclaration | undefined,
  gmOfTeam: (team: string) => string | undefined,
  phase: TradeWindowPhase = 'keepers-locked',
): Controlled[] {
  const who = gm.toUpperCase();
  const kept = new Map(
    (decl?.status === 'submitted' ? decl.keepers : [])
      .filter((k) => !k.farmForfeited)
      .map((k) => [k.player, k]),
  );
  const out: Controlled[] = [];
  for (const r of rows) {
    if ((gmOfTeam(r.team) ?? r.team).toUpperCase() !== who) continue;
    const k = kept.get(r.player);
    const eligible = r.cost !== null;
    /*
     * WHO IS ON THE LIST depends on the phase. Once slates are locked, a man not on one is not
     * this GM's to trade before the draft — he goes back in the pool. Before the lock any
     * keeper-eligible man is a potential keeper and may be dealt as one. In season the roster is
     * the roster.
     */
    if (phase === 'keepers-locked' && !k) continue;
    if (phase === 'keepers-open' && !k && !eligible) continue;
    const costRound = k ? (k.franchiseTagged ? r.tagCost : r.cost) : r.cost;
    out.push({
      name: r.player,
      pos: r.pos || null,
      keeper: Boolean(k),
      costRound,
      kind: 'player',
      note: k
        ? `keeper · costs Rd ${costRound ?? '?'}${k.franchiseTagged ? ' (franchised)' : ''}`
        : phase === 'keepers-open' && eligible
          ? `keeper-eligible · would cost Rd ${costRound}`
          : 'on the roster',
    });
  }
  out.sort((a, b) => Number(b.keeper) - Number(a.keeper) || a.name.localeCompare(b.name));
  return out;
}

/** Farm rights he currently holds — players still in college on his farm roster. */
export function controlledRights(gm: string, farm: readonly FarmRow[]): Controlled[] {
  const who = gm.toUpperCase();
  return farm
    .filter((f) => f.gm.toUpperCase() === who && f.status === 'college' && f.active !== false)
    .map((f) => ({
      name: f.player,
      pos: f.pos ?? null,
      keeper: false,
      costRound: null,
      kind: 'rights' as const,
      note: `farm rights${f.school ? ` · ${f.school}` : ''}`,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** How many farm players a GM holds, for the receiver's cap (2022 §1). */
export function farmCount(gm: string, farm: readonly FarmRow[]): number {
  return controlledRights(gm, farm).length;
}

/** One list for the one button: what he may deal, players and farm rights together. */
export function controlledAssets(
  gm: string,
  rows: readonly RosterRow[],
  farm: readonly FarmRow[],
  decl: KeeperDeclaration | undefined,
  gmOfTeam: (team: string) => string | undefined,
  phase: TradeWindowPhase,
): Controlled[] {
  return [...controlledPlayers(gm, rows, decl, gmOfTeam, phase), ...controlledRights(gm, farm)];
}

/** Which phase the trade form is in, from what the league settings say. */
export function tradePhase(
  inSeason: boolean, keeperDeadline: string | null | undefined, now = Date.now(),
): TradeWindowPhase {
  if (inSeason) return 'in-season';
  const at = keeperDeadline ? Date.parse(keeperDeadline) : NaN;
  return !Number.isNaN(at) && now >= at ? 'keepers-locked' : 'keepers-open';
}
